import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { useGalleryThemeVariables } from '../../lib/galleryCustomisation';
import { cn } from '../../lib/utils';

const buttonStyles = cva(
  'inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-sm px-5 text-sm font-semibold tracking-[-0.01em] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        primary: 'bg-black text-ivory hover:bg-ink focus-visible:outline-black',
        secondary: 'bg-ivory text-ink ring-1 ring-inset ring-ink/20 hover:bg-bone focus-visible:outline-black',
        ghost: 'text-ink hover:bg-ink/10 focus-visible:outline-black',
        danger: 'bg-clay text-white hover:bg-clay/90 focus-visible:outline-clay'
      },
      size: {
        sm: 'h-10 px-3.5',
        md: 'h-11 px-5',
        icon: 'h-11 w-11 px-0'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md'
    }
  }
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  const galleryTheme = useGalleryThemeVariables();
  const resolvedVariant = variant ?? 'primary';
  return (
    <Component
      className={cn(buttonStyles({ variant, size }), galleryTheme && 'client-gallery-button', className)}
      data-gallery-button-variant={galleryTheme ? resolvedVariant : undefined}
      {...props}
    />
  );
}
